import { Inject, Injectable } from "@angular/core";
import { AuthStore } from "app/auth";
import { OutgoingMessageFactory } from "app/chat/outgoing";
import { APP_CONFIG, AppConfig, PersistentStore } from "app/core";
import { USER_STORE, UserStore } from "app/user";
import { Observable, of } from "rxjs";
import { filter, finalize, map, mergeMap, share, take, tap } from "rxjs/operators";
import { ConversationId } from "./conversation-id.model";
import { ConversationApiClient } from "./conversation.apiclient";
import { CONVERSATION_ID_PERSISTENT_STORE } from "./di-tokens";

/** A store for the token used to access a chat conversation. */
@Injectable({ providedIn: "root" })
export class ConversationIdStore {
  /** Only available if the creation of a new conversation is being done */
  private futureId?: Observable<ConversationId>;

  constructor(
    @Inject(APP_CONFIG) config: AppConfig,
    private readonly apiClient: ConversationApiClient,
    private readonly outgoingMessageFactory: OutgoingMessageFactory,
    @Inject(CONVERSATION_ID_PERSISTENT_STORE)
    private readonly persistentStore: PersistentStore<ConversationId>,
    @Inject(USER_STORE) private readonly userStore: UserStore,
    private readonly authStore: AuthStore
  ) {
    if (config.hasResetConversationOnReload) {
      persistentStore.clear();
    }
  }

  private createNew(): Observable<ConversationId> {
    if (!this.futureId) {
      this.persistentStore.clear();
      let conversationId: ConversationId;
      this.futureId = this.apiClient
        .create()
        .pipe(
          tap((data: ConversationId) => {
            this.persistentStore.set(data);
            conversationId = data;
          }),
          // send ping once the conversation is created
          mergeMap(() =>
            this.apiClient.postMessage(
              conversationId,
              this.outgoingMessageFactory.makeInitializationMessage(),
              this.userStore.get().sessionId
            )
          ),
          map(() => conversationId),
          finalize(() => (this.futureId = undefined))
        )
        .pipe(share());
    }
    return this.futureId;
  }

  /**
   * @return The current conversation ID, or the ID of a new conversation if none exists,
   * once authentication allows it
   * @note Waits for the same terminal auth states as the application start, so no conversation
   * call leaves while a session is being verified.
   */
  get(): Observable<ConversationId> {
    return this.authStore.state$.pipe(
      filter((state) => state.status === "authenticated" || state.status === "not-required"),
      take(1),
      mergeMap(() => this.persistentStore.get()),
      mergeMap((conversationId) =>
        conversationId === null ? this.createNew() : of(conversationId)
      )
    );
  }

  /** Force the currently stored token cleaning */
  public invalidate(): void {
    this.persistentStore.clear();
  }
}
